import React, { useState, useEffect } from 'react';
import { aiGovernanceApi } from '../api/client';

const styles: Record<string, React.CSSProperties> = {
  page: { fontFamily: 'Inter, system-ui, sans-serif' },
  tabs: { display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 24 },
  tab: { padding: '10px 20px', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', border: 'none', background: 'none', borderBottom: '2px solid transparent', marginBottom: -2, transition: 'all 0.15s' },
  tabActive: { color: '#4f46e5', borderBottomColor: '#4f46e5' },
  card: { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  infoCard: { background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', borderRadius: 12, border: '1px solid #c7d2fe', padding: 20, marginBottom: 24 },
  infoTitle: { fontSize: 14, fontWeight: 700, color: '#3730a3', marginBottom: 12 },
  infoSection: { marginBottom: 10 },
  infoLabel: { fontSize: 11, fontWeight: 600, color: '#4f46e5', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 },
  infoList: { margin: 0, paddingLeft: 18, fontSize: 12, color: '#1e1b4b', lineHeight: 1.8 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  cardMeta: { fontSize: 12, color: '#64748b', marginBottom: 4 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.3 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: { textAlign: 'left' as const, padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.4 },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' },
  formGroup: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, minHeight: 70, resize: 'vertical' as const, boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const },
  btn: { padding: '9px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  loading: { textAlign: 'center' as const, padding: 40, color: '#64748b', fontSize: 13 },
  error: { textAlign: 'center' as const, padding: 40, color: '#dc2626', fontSize: 13 },
};

const riskColors: Record<string, React.CSSProperties> = {
  low: { background: '#dcfce7', color: '#166534' },
  medium: { background: '#fef9c3', color: '#854d0e' },
  high: { background: '#fee2e2', color: '#991b1b' },
  critical: { background: '#fce7f3', color: '#831843' },
};

const dispositionColors: Record<string, React.CSSProperties> = {
  confirmed: { background: '#dcfce7', color: '#166534' },
  corrected: { background: '#dbeafe', color: '#1e40af' },
  rejected: { background: '#fee2e2', color: '#991b1b' },
  unsafe: { background: '#f3e8ff', color: '#6b21a8' },
};

export default function AIGovernance() {
  const [tab, setTab] = useState<'usecases' | 'registry' | 'feedback'>('usecases');
  const [inventory, setInventory] = useState<any>(null);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Feedback form state
  const [fbForm, setFbForm] = useState({ use_case_id: '', disposition: 'confirmed', feedback_text: '', original_output: '', corrected_output: '' });
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbSuccess, setFbSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [inv, fb] = await Promise.all([
        aiGovernanceApi.inventory(),
        aiGovernanceApi.listFeedback(),
      ]);
      setInventory(inv);
      setFeedback(Array.isArray(fb) ? fb : fb?.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load AI governance data');
    }
    setLoading(false);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fbForm.use_case_id || !fbForm.feedback_text) return;
    setFbSubmitting(true);
    setFbSuccess('');
    try {
      await aiGovernanceApi.submitFeedback(fbForm);
      setFbSuccess('Feedback submitted successfully.');
      setFbForm({ use_case_id: '', disposition: 'confirmed', feedback_text: '', original_output: '', corrected_output: '' });
      const fb = await aiGovernanceApi.listFeedback();
      setFeedback(Array.isArray(fb) ? fb : fb?.items || []);
    } catch (err: any) {
      setFbSuccess('');
      setError(err.message || 'Failed to submit feedback');
    }
    setFbSubmitting(false);
  };

  if (loading) return <div style={styles.loading}>Loading AI Governance data...</div>;
  if (error && !inventory) return <div style={styles.error}>{error}</div>;

  const useCases = inventory?.use_cases || [];
  const modelRegistry = inventory?.model_registry || [];
  const rules = inventory?.governance_rules || {};

  return (
    <div style={styles.page}>
      {/* Governance Rules Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.infoTitle}>AI Governance Rules</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {rules.ai_cannot && (
            <div style={styles.infoSection}>
              <div style={styles.infoLabel}>AI Cannot</div>
              <ul style={styles.infoList}>
                {rules.ai_cannot.map((item: string, i: number) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}
          {rules.ai_must && (
            <div style={styles.infoSection}>
              <div style={styles.infoLabel}>AI Must</div>
              <ul style={styles.infoList}>
                {rules.ai_must.map((item: string, i: number) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}
          {rules.data_policy && (
            <div style={styles.infoSection}>
              <div style={styles.infoLabel}>Data Policy</div>
              <ul style={styles.infoList}>
                {(Array.isArray(rules.data_policy) ? rules.data_policy : [rules.data_policy]).map((item: string, i: number) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs} role="tablist" aria-label="AI Governance sections">
        <button
          role="tab"
          aria-selected={tab === 'usecases'}
          style={{ ...styles.tab, ...(tab === 'usecases' ? styles.tabActive : {}) }}
          onClick={() => setTab('usecases')}
        >
          Use Cases
        </button>
        <button
          role="tab"
          aria-selected={tab === 'registry'}
          style={{ ...styles.tab, ...(tab === 'registry' ? styles.tabActive : {}) }}
          onClick={() => setTab('registry')}
        >
          Model Registry
        </button>
        <button
          role="tab"
          aria-selected={tab === 'feedback'}
          style={{ ...styles.tab, ...(tab === 'feedback' ? styles.tabActive : {}) }}
          onClick={() => setTab('feedback')}
        >
          Feedback
        </button>
      </div>

      {/* Use Cases Tab */}
      {tab === 'usecases' && (
        <div style={styles.grid}>
          {useCases.length === 0 && <div style={styles.loading}>No use cases found.</div>}
          {useCases.map((uc: any, i: number) => (
            <div key={uc.id || i} style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={styles.cardTitle}>{uc.name}</div>
                {uc.risk_level && (
                  <span style={{ ...styles.badge, ...(riskColors[uc.risk_level] || riskColors.medium) }}>
                    {uc.risk_level}
                  </span>
                )}
              </div>
              <div style={styles.cardMeta}><strong>Purpose:</strong> {uc.purpose}</div>
              <div style={styles.cardMeta}><strong>Model:</strong> {uc.model}</div>
              <div style={styles.cardMeta}><strong>Human Review:</strong> {uc.human_review}</div>
            </div>
          ))}
        </div>
      )}

      {/* Model Registry Tab */}
      {tab === 'registry' && (
        <div style={styles.grid}>
          {modelRegistry.length === 0 && <div style={styles.loading}>No models registered.</div>}
          {modelRegistry.map((m: any, i: number) => (
            <div key={m.model_name || i} style={styles.card}>
              <div style={styles.cardTitle}>{m.model_name}</div>
              <div style={styles.cardMeta}><strong>Provider:</strong> {m.provider}</div>
              <div style={styles.cardMeta}><strong>Version:</strong> {m.version}</div>
              {m.parameters && <div style={styles.cardMeta}><strong>Parameters:</strong> {m.parameters}</div>}
              {m.context_window && <div style={styles.cardMeta}><strong>Context Window:</strong> {m.context_window}</div>}
              {m.capabilities && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Capabilities</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(Array.isArray(m.capabilities) ? m.capabilities : [m.capabilities]).map((c: string, ci: number) => (
                      <span key={ci} style={{ ...styles.badge, background: '#e0e7ff', color: '#3730a3' }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {m.limitations && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Limitations</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {Array.isArray(m.limitations) ? m.limitations.join('; ') : m.limitations}
                  </div>
                </div>
              )}
              {m.data_handling && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Data Handling</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {Array.isArray(m.data_handling) ? m.data_handling.join('; ') : m.data_handling}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Feedback Tab */}
      {tab === 'feedback' && (
        <div>
          {/* Feedback Table */}
          <div style={{ ...styles.card, overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Disposition</th>
                  <th style={styles.th}>Use Case</th>
                  <th style={styles.th}>Feedback</th>
                  <th style={styles.th}>Submitted By</th>
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {feedback.length === 0 && (
                  <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#94a3b8' }}>No feedback submitted yet.</td></tr>
                )}
                {feedback.map((fb: any, i: number) => (
                  <tr key={fb.id || i}>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...(dispositionColors[fb.disposition] || {}) }}>
                        {fb.disposition}
                      </span>
                    </td>
                    <td style={styles.td}>{fb.use_case || fb.use_case_id}</td>
                    <td style={{ ...styles.td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fb.feedback_text}</td>
                    <td style={styles.td}>{fb.submitted_by}</td>
                    <td style={styles.td}>{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Submit Feedback Form */}
          <div style={{ ...styles.card, marginTop: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Submit Feedback</div>
            {fbSuccess && <div style={{ padding: '8px 12px', background: '#dcfce7', color: '#166534', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{fbSuccess}</div>}
            <form onSubmit={handleFeedbackSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="fb-usecase">Use Case</label>
                  <select
                    id="fb-usecase"
                    style={styles.select}
                    value={fbForm.use_case_id}
                    onChange={e => setFbForm({ ...fbForm, use_case_id: e.target.value })}
                    required
                  >
                    <option value="">Select use case...</option>
                    {useCases.map((uc: any) => (
                      <option key={uc.id || uc.name} value={uc.id || uc.name}>{uc.name}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="fb-disposition">Disposition</label>
                  <select
                    id="fb-disposition"
                    style={styles.select}
                    value={fbForm.disposition}
                    onChange={e => setFbForm({ ...fbForm, disposition: e.target.value })}
                  >
                    <option value="confirmed">Confirmed</option>
                    <option value="corrected">Corrected</option>
                    <option value="rejected">Rejected</option>
                    <option value="unsafe">Unsafe</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="fb-text">Feedback Text</label>
                <textarea
                  id="fb-text"
                  style={styles.textarea}
                  value={fbForm.feedback_text}
                  onChange={e => setFbForm({ ...fbForm, feedback_text: e.target.value })}
                  placeholder="Describe your feedback on the AI output..."
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="fb-original">Original Output</label>
                <textarea
                  id="fb-original"
                  style={styles.textarea}
                  value={fbForm.original_output}
                  onChange={e => setFbForm({ ...fbForm, original_output: e.target.value })}
                  placeholder="Paste the original AI output here..."
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="fb-corrected">Corrected Output (optional)</label>
                <textarea
                  id="fb-corrected"
                  style={styles.textarea}
                  value={fbForm.corrected_output}
                  onChange={e => setFbForm({ ...fbForm, corrected_output: e.target.value })}
                  placeholder="If correcting, provide the expected output..."
                />
              </div>
              <button
                type="submit"
                style={{ ...styles.btn, ...(fbSubmitting ? styles.btnDisabled : {}) }}
                disabled={fbSubmitting}
              >
                {fbSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
